// Search (command palette, element 046 — Ctrl/⌘+K or "/"): screens the person may open, people and records from the
// server search (GET /api/v1/search — filtered by the reader's permissions there: pseudonyms match only as pseudonyms,
// others' records only by number), glossary terms (plain words, original term, English, aliases, original
// event/setting names; 초성 works) and the last 5 searches of this browser.
// Keyboard: ↑↓ move, Enter opens, Esc clears the text first, then closes. Korean IME composition is respected.
import { SOURCE_OF_TOOL } from '@aiax-hub/contracts';
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useHeaderSearch } from '../api/shell';
import { usePerms } from '../auth/AuthContext';
import { menuFor } from '../auth/permissions';
import { SUGGESTED_TERMS, plainOf, searchTerms, termOf } from '../glossary';
import { useFmt, useLang, useT } from '../i18n';
import { cls, match, store } from '../lib/util';
import { Icon, type IconName } from '../ui/Icon';
import { raiseNotices, toast } from '../ui/layers';
import { sourceInfo } from '../ui/sources';

interface PaletteItem {
  kind: 'screen' | 'term' | 'recent' | 'clearRecent' | 'person' | 'log';
  label: string;
  sub: string;
  icon: IconName;
  href?: string;
  q?: string;
}
interface PaletteGroup {
  key: 'recent' | 'screens' | 'terms' | 'people' | 'logs';
  items: PaletteItem[];
}

const RECENT_KEY = 'palette.recent';
const RECENT_MAX = 5;
function recentSearches(): string[] {
  try {
    const a: unknown = JSON.parse(store.get(RECENT_KEY) || '[]');
    return Array.isArray(a) ? a.filter((x): x is string => typeof x === 'string' && !!x.trim()).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}
const rememberSearch = (q: string) => store.set(RECENT_KEY, JSON.stringify([q, ...recentSearches().filter((x) => x !== q)].slice(0, RECENT_MAX)));

function Highlight({ text, q }: { text: string; q: string }) {
  const m = q ? match(text, q) : null;
  if (!q || !m || !m.length) return <>{text}</>;
  return (
    <>
      {text.slice(0, m.index)}
      <mark>{text.slice(m.index, m.index + m.length)}</mark>
      {text.slice(m.index + m.length)}
    </>
  );
}

type DialogLike = HTMLDialogElement & { showModal?: () => void };

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const fmt = useFmt();
  const lang = useLang();
  const perms = usePerms();
  const navigate = useNavigate();
  const dlgRef = useRef<DialogLike>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevFocus = useRef<Element | null>(null);
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [act, setAct] = useState(0);
  const [recentTick, setRecentTick] = useState(0);
  const found = useHeaderSearch(q, open);

  useEffect(() => {
    const tm = setTimeout(() => setQ(text.trim()), 100);
    return () => clearTimeout(tm);
  }, [text]);

  const groups = useMemo<PaletteGroup[]>(() => {
    const out: PaletteGroup[] = [];
    const screens = menuFor(perms)
      .map<PaletteItem>((m) => ({ kind: 'screen', label: t(m.key), sub: t(m.key + '.desc'), href: '/' + m.path, icon: m.icon }))
      .filter((x) => !q || match(x.label, q) || match(x.sub, q));
    if (screens.length) out.push({ key: 'screens', items: q ? screens : screens.slice(0, 7) });
    const res = q && found.data ? found.data : null;
    const nameOf = (p: { name: string | null; pseudonym: string }) => p.name ?? t('person.pseudo', { code: p.pseudonym });
    // people: names follow the reader's real-name permission (pseudonym otherwise) — the server already decided
    const people = (res?.people ?? []).map<PaletteItem>((x) => ({
      kind: 'person',
      label: nameOf(x.person),
      sub: [x.team?.name, x.title].filter(Boolean).join(' · '),
      href: x.canOpen ? '/people/' + encodeURIComponent(x.person.id) : '/people',
      icon: 'user',
    }));
    if (people.length) out.push({ key: 'people', items: people });
    const terms = (q ? searchTerms(q, 6) : SUGGESTED_TERMS.map((s) => termOf(s)).filter((g): g is NonNullable<typeof g> => !!g).slice(0, 4)).map<PaletteItem>((g) => ({
      kind: 'term',
      label: plainOf(g, lang),
      sub: lang === 'en' ? g.termEn : g.term,
      href: '/glossary/' + g.slug,
      icon: 'book',
    }));
    if (terms.length) out.push({ key: 'terms', items: terms });
    // records: a title only for my own records; others by number (plan 7.5.4)
    const logs = (res?.records ?? []).map<PaletteItem>((x) => {
      const src = sourceInfo(SOURCE_OF_TOOL[x.tool]);
      return {
        kind: 'log',
        label: x.title ?? t('logs.crumb.no', { no: String(x.record.no) }),
        sub: [src.label, nameOf(x.person), fmt.dateTime(x.startedAt)].join(' · '),
        href: '/logs/' + x.record.no,
        icon: src.path === 'api' ? 'key' : 'terminal',
      };
    });
    if (logs.length) out.push({ key: 'logs', items: logs });
    const recent = q ? [] : recentSearches();
    if (recent.length)
      out.unshift({
        key: 'recent',
        items: [
          ...recent.map<PaletteItem>((rq) => ({ kind: 'recent', label: rq, sub: t('palette.recent.sub'), icon: 'clock', q: rq })),
          { kind: 'clearRecent', label: t('palette.recent.clear'), sub: t('palette.recent.clearSub'), icon: 'x' },
        ],
      });
    return out;
    // recentTick: re-read the browser's recent searches after clearing them
  }, [perms, q, t, fmt, lang, recentTick, found.data]);
  const flat = groups.flatMap((g) => g.items);

  useEffect(() => setAct(0), [q, recentTick]);

  useLayoutEffect(() => {
    const dlg = dlgRef.current;
    if (!open || !dlg) return;
    prevFocus.current = document.activeElement;
    try {
      if (typeof dlg.showModal === 'function') dlg.showModal();
      else dlg.setAttribute('open', '');
    } catch {
      dlg.setAttribute('open', '');
    }
    dlg.setAttribute('data-modal', '');
    document.documentElement.classList.add('has-dlg');
    raiseNotices();
    setText('');
    setQ('');
    inputRef.current?.focus();
    return () => {
      try {
        dlg.close?.();
      } catch {
        dlg.removeAttribute('open');
      }
      dlg.removeAttribute('open');
      dlg.removeAttribute('data-modal');
      document.documentElement.classList.remove('has-dlg');
      raiseNotices();
      const back = prevFocus.current as HTMLElement | null;
      if (back && document.contains(back)) back.focus({ preventScroll: true });
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    document.getElementById(`pal-o-${act}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [act, open]);

  const run = (i: number) => {
    const it = flat[i];
    if (!it) return;
    if (it.kind === 'recent') {
      setText(it.q ?? '');
      setQ(it.q ?? '');
      inputRef.current?.focus();
      return;
    }
    if (it.kind === 'clearRecent') {
      store.set(RECENT_KEY, '[]');
      setRecentTick((n) => n + 1);
      inputRef.current?.focus();
      toast(t('palette.recent.cleared'), 'ok');
      return;
    }
    if (!it.href) return;
    if (text.trim()) rememberSearch(text.trim());
    onClose();
    navigate(it.href);
  };
  const clearOrClose = () => {
    if (text) {
      setText('');
      setQ('');
    } else onClose();
  };

  if (!open || typeof document === 'undefined') return null;
  let n = -1;
  return createPortal(
    <dialog
      ref={dlgRef}
      className="palette"
      aria-label={t('palette.title')}
      onCancel={(e) => {
        e.preventDefault();
        clearOrClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="palette__field">
        <Icon name="search" />
        <input
          ref={inputRef}
          className="palette__input"
          id="pal-in"
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls="pal-list"
          aria-autocomplete="list"
          aria-activedescendant={flat.length ? `pal-o-${act}` : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder={t('palette.placeholder')}
          aria-label={t('palette.title')}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setAct((a) => Math.min(flat.length - 1, a + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setAct((a) => Math.max(0, a - 1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              run(act);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              clearOrClose();
            }
          }}
        />
        <kbd className="kbd">Esc</kbd>
      </div>
      <div className="palette__list" id="pal-list" role="listbox" aria-label={t('palette.title')}>
        {groups.map((g) => (
          <div key={g.key} role="group" aria-labelledby={`pal-g-${g.key}`} className="palette__group">
            <p className="palette__g" id={`pal-g-${g.key}`} role="presentation">
              {t('palette.group.' + g.key)}
            </p>
            {g.items.map((it) => {
              n++;
              const i = n;
              return (
                <Fragment key={`${g.key}-${i}`}>
                  <div
                    className={cls('palette__op', it.kind === 'clearRecent' && 'palette__op--clear')}
                    role="option"
                    id={`pal-o-${i}`}
                    aria-selected={i === act}
                    data-i={i}
                    onPointerMove={() => {
                      if (i !== act) setAct(i);
                    }}
                    onClick={() => {
                      setAct(i);
                      run(i);
                    }}
                  >
                    <span className="palette__ic">
                      <Icon name={it.icon} />
                    </span>
                    <span className="palette__txt">
                      <span>
                        <Highlight text={it.label} q={q} />
                      </span>
                      <small>{it.sub}</small>
                    </span>
                    {it.href || it.kind === 'recent' ? <Icon name="chevR" className="palette__go" /> : null}
                  </div>
                </Fragment>
              );
            })}
          </div>
        ))}
        {!flat.length ? (
          <div className="palette__empty" role="presentation">
            <b>{t('palette.empty', { q })}</b>
            <span>{t('palette.emptyHint')}</span>
          </div>
        ) : null}
      </div>
      <div className="palette__foot" aria-hidden="true">
        <span>
          <kbd className="kbd">↑↓</kbd>
          {t('palette.hint.move')}
        </span>
        <span>
          <kbd className="kbd">↵</kbd>
          {t('palette.hint.open')}
        </span>
        <span>
          <kbd className="kbd">Esc</kbd>
          {t('palette.hint.close')}
        </span>
      </div>
    </dialog>,
    document.body,
  );
}
